'use client';

import { useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { RefreshCw, Loader2, Send, BarChart3, Mail, Eye, Flame, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useThemePrefsStore } from '@/shared/stores/theme-prefs-store';
import { useBriefing, useRefreshBriefing } from '../hooks/use-briefing';
import { briefingApi } from '../services/briefing-api';
import type { BriefingEntity, BriefingAction } from '../services/briefing-api';
import { DocumentPreviewModal, registerDocumentType } from '@/features/document-preview';

// Înregistrăm tipul „invoice" și aici (idempotent) ca drawer-ul de preview să poată
// deschide factura PESTE dashboard, fără să navigăm la pagina-listă de facturi.
registerDocumentType('invoice', () => import('@/features/invoices/components/invoice-preview-config'));

const ICON_MAP: Record<string, any> = {
  send: Send,
  chart: BarChart3,
  mail: Mail,
  eye: Eye,
  flame: Flame,
};

// Rezolvă href-ul unei entități: ori cel explicit din răspunsul AI, ori un fallback
// bazat pe type. Doar entitățile rezolvate la href vor fi subliniate (clickabile).
// Sumele și datele rămân doar bold (fără underline) — emphasis fără false affordance.
function resolveEntityHref(entity: BriefingEntity): string | null {
  if (entity.href) return entity.href;
  if (entity.type === 'client') {
    if (entity.id) return '/clienti/' + entity.id;
    return '/clienti?search=' + encodeURIComponent(entity.label);
  }
  if (entity.type === 'invoice') {
    if (entity.id) return '/facturi?view=' + entity.id;
  }
  if (entity.type === 'amount') {
    // Heuristic fallback când AI nu a inclus href explicit pentru sumă.
    // Tone-ul ne dă un hint: 'urgent' / 'warning' = restanță → /facturi?status=overdue.
    // Restul sumelor → lista generală de facturi.
    if (entity.tone === 'urgent' || entity.tone === 'warning') {
      return '/facturi?status=overdue';
    }
    return '/facturi';
  }
  return null;
}

function EntityText({ text, entities, onOpenInvoice }: { text: string; entities: BriefingEntity[]; onOpenInvoice?: (id: string) => void }) {
  // Replace entity.label occurrences in text with clickable spans
  const parts = useMemo(() => {
    if (!entities || entities.length === 0) return [{ type: 'text' as const, content: text }];
    const result: Array<{ type: 'text' | 'entity'; content: string; entity?: BriefingEntity }> = [];
    let remaining = text;
    // Sort entities by label length desc to avoid nested match issues
    const sorted = [...entities].sort((a, b) => b.label.length - a.label.length);
    while (remaining.length > 0) {
      let earliestIdx = -1;
      let earliestEntity: BriefingEntity | null = null;
      for (const e of sorted) {
        const idx = remaining.indexOf(e.label);
        if (idx !== -1 && (earliestIdx === -1 || idx < earliestIdx)) {
          earliestIdx = idx;
          earliestEntity = e;
        }
      }
      if (earliestIdx === -1 || !earliestEntity) {
        result.push({ type: 'text', content: remaining });
        break;
      }
      if (earliestIdx > 0) {
        result.push({ type: 'text', content: remaining.slice(0, earliestIdx) });
      }
      result.push({ type: 'entity', content: earliestEntity.label, entity: earliestEntity });
      remaining = remaining.slice(earliestIdx + earliestEntity.label.length);
    }
    return result;
  }, [text, entities]);

  return (
    <>
      {parts.map((p, i) => {
        if (p.type === 'text') return <span key={i}>{p.content}</span>;

        // Numele de clienți rămân text normal — sunt subiecte ale narațiunii,
        // nu identifiers de evidențiat. User-ul navighează prin documente sau widget-uri dedicate.
        if (p.entity?.type === 'client') {
          return <span key={i}>{p.content}</span>;
        }

        const tone = p.entity?.tone || 'default';
        const toneColors: Record<string, string> = {
          urgent: 'text-red-300',
          positive: 'text-emerald-300',
          warning: 'text-amber-300',
          default: 'text-orange-200',
        };
        const toneHover: Record<string, string> = {
          urgent: 'decoration-red-400/40 hover:bg-red-500/15 hover:text-red-200',
          positive: 'decoration-emerald-400/40 hover:bg-emerald-500/15 hover:text-emerald-200',
          warning: 'decoration-amber-400/40 hover:bg-amber-500/15 hover:text-amber-200',
          default: 'decoration-orange-300/30 hover:bg-orange-500/15 hover:text-orange-100',
        };
        const colorCls = toneColors[tone] || toneColors.default;
        const href = p.entity ? resolveEntityHref(p.entity) : null;
        const cls = 'underline underline-offset-[3px] decoration-[1.5px] font-medium cursor-pointer transition-colors px-0.5 rounded-sm ' + colorCls + ' ' + (toneHover[tone] || toneHover.default);

        // Factură specifică: deschidem drawer-ul de preview PESTE dashboard (fără navigare).
        if (onOpenInvoice && p.entity?.type === 'invoice' && p.entity.id) {
          const invId = p.entity.id;
          return (
            <button key={i} type="button" onClick={() => onOpenInvoice(invId)} className={cls}>
              {p.content}
            </button>
          );
        }

        if (href) {
          // Clickabil: underline + hover (face ceva la click)
          return (
            <Link key={i} href={href} className={cls}>
              {p.content}
            </Link>
          );
        }
        // Non-clickabil (sume, date): doar emphasis (bold + culoare), fără underline / cursor
        return <span key={i} className={'font-semibold ' + colorCls}>{p.content}</span>;
      })}
    </>
  );
}

const ACTION_HREF_MAP: Record<string, string> = {
  // Overdue / restanțe
  'see-overdue': '/facturi?status=overdue',
  'view-overdue': '/facturi?status=overdue',
  // Reminders
  'send-reminder': '/facturi',
  'reminder': '/facturi',
  'trimite-reminder': '/facturi',
  // ANAF / e-Factura
  'anaf-send': '/efactura',
  'send-anaf': '/efactura',
  'view-efactura': '/efactura',
  // Drafturi
  'see-drafts': '/facturi?status=draft',
  'view-drafts': '/facturi?status=draft',
  // Facturi — listare / creare
  'see-invoices': '/facturi',
  'view-invoices': '/facturi',
  'view-invoice': '/facturi',
  'see-invoice': '/facturi',
  'create-invoice': '/facturi/nou',
  'new-invoice': '/facturi/nou',
  // Proforme
  'see-proformas': '/proforme',
  'view-proformas': '/proforme',
  'see-proforma': '/proforme',
  'view-proforma': '/proforme',
  'create-proforma': '/proforme/nou',
  'new-proforma': '/proforme/nou',
  // Avize
  'see-avize': '/avize',
  'view-avize': '/avize',
  'see-aviz': '/avize',
  'view-aviz': '/avize',
  'create-aviz': '/avize/nou',
  'new-aviz': '/avize/nou',
};

// Fallback resolver — când action.id urmează un pattern cunoscut dar nu e în map
function resolveFallbackHref(actionId: string): string | null {
  const id = actionId.toLowerCase();
  if (id.includes('proforma') || id.includes('proforme')) return '/proforme';
  if (id.includes('aviz')) return '/avize';
  if (id.includes('overdue') || id.includes('restant')) return '/facturi?status=overdue';
  if (id.includes('draft')) return '/facturi?status=draft';
  if (id.includes('efactura') || id.includes('anaf')) return '/efactura';
  if (id.includes('invoice') || id.includes('factur')) return '/facturi';
  return null;
}

function ActionButton({ action }: { action: BriefingAction }) {
  const Icon = ICON_MAP[action.icon] || Send;
  const baseClasses = 'inline-flex items-center gap-2 h-10 px-3.5 rounded-[10px] text-[0.8125rem] font-medium transition-all';
  const variantClasses =
    action.variant === 'primary'
      ? 'bg-primary text-white border border-primary hover:bg-primary-600 font-semibold sf-recap-primary-btn'
      : 'bg-white/5 text-neutral-50 border border-white/10 hover:bg-white/10 hover:border-white/20 hover:-translate-y-px';

  const href = action.href || ACTION_HREF_MAP[action.id] || resolveFallbackHref(action.id);
  if (href) {
    return (
      <Link href={href} className={baseClasses + ' ' + variantClasses}>
        <Icon className="h-3.5 w-3.5 opacity-80" />
        {action.label}
      </Link>
    );
  }
  // No href resolved — button is effectively inert; log for debugging
  if (typeof window !== 'undefined') {
    console.warn('[AiBriefing] Action without resolvable href:', action);
  }
  return (
    <button type="button" className={baseClasses + ' ' + variantClasses} disabled>
      <Icon className="h-3.5 w-3.5 opacity-80" />
      {action.label}
    </button>
  );
}

export function AiBriefing() {
  const { data, isLoading, isFetching } = useBriefing();
  const refresh = useRefreshBriefing();
  // Factura clickuită din briefing se deschide în drawer PESTE dashboard (fără navigare).
  const [drawerInvoiceId, setDrawerInvoiceId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);
  const [feedbackStatus, setFeedbackStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Load cached feedback per briefing generatedAt from localStorage
  useEffect(() => {
    if (!data?.generatedAt) return;
    try {
      const cached = localStorage.getItem('sf-briefing-fb-' + data.generatedAt);
      if (cached === 'up' || cached === 'down') setFeedback(cached);
      else setFeedback(null);
    } catch {}
  }, [data?.generatedAt]);

  const submitFeedback = async (rating: 'up' | 'down') => {
    if (!data?.generatedAt || !data?.context) return;
    // Toggle off if clicking same rating
    if (feedback === rating) return;
    setFeedback(rating);
    setFeedbackStatus('saving');
    try {
      await briefingApi.feedback({ context: data.context, rating, generatedAt: data.generatedAt });
      try { localStorage.setItem('sf-briefing-fb-' + data.generatedAt, rating); } catch {}
      setFeedbackStatus('saved');
      setTimeout(() => setFeedbackStatus('idle'), 2500);
    } catch {
      setFeedbackStatus('error');
      setTimeout(() => { setFeedbackStatus('idle'); setFeedback(null); }, 2500);
    }
  };

  const generatedAgo = useMemo(() => {
    if (!data?.generatedAt) return null;
    const diff = Math.floor((Date.now() - new Date(data.generatedAt).getTime()) / 60000);
    if (diff < 1) return 'acum câteva secunde';
    if (diff === 1) return 'acum 1 minut';
    if (diff < 60) return 'acum ' + diff + ' minute';
    const h = Math.floor(diff / 60);
    return 'acum ' + h + (h === 1 ? ' oră' : ' ore');
  }, [data?.generatedAt]);

  const contextLabel = useMemo(() => {
    const c = data?.context;
    if (c === 'morning') return 'SUMAR · DIMINEAȚĂ';
    if (c === 'afternoon') return 'SUMAR · DUPĂ-AMIAZĂ';
    if (c === 'evening') return 'SUMAR · SEARĂ';
    if (c === 'weekend') return 'SUMAR · WEEKEND';
    return 'SUMAR';
  }, [data?.context]);

  const headerStyle = useThemePrefsStore((s) => s.headerStyle);

  // Card background adapts to Setări > Aspect > headerStyle
  const cardBg = useMemo(() => {
    switch (headerStyle) {
      case 'solid-bar':
        return 'linear-gradient(135deg, rgb(var(--primary-900)) 0%, rgb(var(--primary-800)) 55%, #1A1714 100%)';
      case 'gradient-linear':
        return 'linear-gradient(to right, rgb(var(--primary-800)) 0%, rgb(var(--primary-900) / 0.8) 28%, #1A1714 65%, #2C2520 100%)';
      case 'outline':
      case 'minimal':
        return 'linear-gradient(135deg, #1A1714 0%, #2C2520 45%, #3B2E26 75%, #4A382B 100%)';
      case 'gradient-radial':
      default:
        return [
          'radial-gradient(ellipse 100% 110% at 0% 0%, rgb(var(--primary-800) / 0.55) 0%, rgb(var(--primary-900) / 0.35) 30%, transparent 60%)',
          'linear-gradient(135deg, #1A1714 0%, #2C2520 45%, #3B2E26 75%, #4A382B 100%)',
        ].join(', ');
    }
  }, [headerStyle]);

  const outlineAccent = headerStyle === 'outline';


  if (isLoading) {
    return (
      <div className="relative overflow-hidden rounded-[20px] px-10 py-9 text-neutral-50 shadow-[0_12px_40px_rgba(15,14,15,0.18)]" style={{ background: cardBg }}>
        <div className="flex items-center gap-3 text-white/60">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Se pregătește sumarul...</span>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <>
    <article
      className={"relative overflow-hidden rounded-[20px] px-10 py-9 text-neutral-50 shadow-[0_12px_40px_rgba(15,14,15,0.18)]" + (outlineAccent ? " border-l-[4px]" : "")}
      style={outlineAccent
        ? { background: cardBg, borderLeftColor: 'rgb(var(--primary-500))', fontFamily: 'var(--font-poppins), sans-serif' }
        : { background: cardBg, fontFamily: 'var(--font-poppins), sans-serif' }}
    >
      {/* Decorative gradient orbs */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-[120px] -right-[80px] h-[320px] w-[320px] blur-[10px]"
        style={{ background: 'radial-gradient(circle, rgb(var(--primary-500) / 0.4) 0%, rgb(var(--primary-500) / 0.15) 35%, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-[200px] -left-[100px] h-[400px] w-[400px] blur-[20px]"
        style={{ background: 'radial-gradient(circle, rgb(var(--primary-700) / 0.2) 0%, rgb(var(--primary-700) / 0.06) 40%, transparent 70%)' }}
      />

      {/* Shimmer progress line */}
      <div className="absolute top-0 left-0 right-0 h-[3px] overflow-hidden" style={{ background: 'linear-gradient(90deg, transparent, rgba(251,249,244,0.1), transparent)' }}>
        <div
          className="absolute top-0 left-0 h-full w-[68%] bg-[length:200%_100%] animate-[shine_3s_linear_infinite]"
          style={{ backgroundImage: 'linear-gradient(90deg, rgb(var(--primary-500)), rgb(var(--primary-200)), rgb(var(--primary-500)))' }}
        />
      </div>

      <div className="relative z-[2]">
        {/* Meta strip — Poppins Medium (accente/etichete, per mockup) */}
        <div className="mb-6 flex items-center justify-between gap-4 text-[0.6875rem] font-medium uppercase tracking-[0.1em] text-white/60">
          <div className="inline-flex items-center gap-2.5">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1"
              style={{ background: 'rgb(var(--primary-500) / 0.22)', color: 'rgb(var(--primary-200))' }}
            >
              <span className="h-[5px] w-[5px] animate-pulse rounded-full" style={{ background: 'rgb(var(--primary-300))' }} />
              {contextLabel}
            </span>
            {generatedAgo && <span className="text-white/50">actualizat {generatedAgo}</span>}
          </div>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => refresh()}
              disabled={isFetching}
              title="Regenerează sumarul"
              className="sf-recap-icon-btn grid h-7 w-7 place-items-center rounded-[7px] border border-white/10 bg-white/5 text-white/50 transition-all disabled:opacity-50"
            >
              <RefreshCw className={'h-3 w-3 ' + (isFetching ? 'animate-spin' : '')} />
            </button>
          </div>
        </div>

        {/* Greeting — Poppins SemiBold (decizie owner 2026-07-22, aliniat mockup).
            Dimensiune redusă față de era-Fraunces (2.75rem): Poppins e mai lat/geometric. */}
        <h2 className="m-0 mb-5 text-[2.25rem] font-semibold leading-[1.15] tracking-[-0.01em] text-neutral-50">
          {data.greeting}
        </h2>

        {/* Paragraphs with entities — Poppins Regular */}
        <div className="mb-5 max-w-[900px] text-[1.0625rem] leading-[1.65] text-white/90">
          {data.paragraphs.map((p, i) => (
            <p key={i} className="m-0 mb-3 last:mb-0">
              <EntityText text={p.text} entities={p.entities || []} onOpenInvoice={setDrawerInvoiceId} />
            </p>
          ))}
        </div>

        {/* Actions */}
        {(data.actions.length > 0 || data.streak) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2 border-t border-white/10 pt-5">
            {data.actions.map((a) => (
              <ActionButton key={a.id} action={a} />
            ))}
            <span className="flex-1" />
            {data.streak && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[0.6875rem] font-medium"
                style={{
                  borderColor: 'rgb(var(--primary-200) / 0.25)',
                  background: 'rgb(var(--primary-200) / 0.15)',
                  color: 'rgb(var(--primary-200))',
                }}
              >
                <Flame className="h-3 w-3" />
                {data.streak.label}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Feedback bottom right */}
      <div className="absolute bottom-3.5 right-5 z-[3] flex items-center gap-2 text-[0.6875rem] tracking-wide text-white/40">
        <span>
          {feedbackStatus === 'saved' ? 'Mulțumim!' : feedbackStatus === 'error' ? 'Eroare — reîncearcă' : 'A fost util?'}
        </span>
        <button
          type="button"
          title="Da"
          onClick={() => submitFeedback('up')}
          disabled={feedbackStatus === 'saving'}
          aria-pressed={feedback === 'up'}
          className={
            'grid h-6 w-6 place-items-center rounded-md border transition-all disabled:opacity-50 ' +
            (feedback === 'up'
              ? 'sf-recap-fb-active'
              : 'sf-recap-icon-btn border-white/6 bg-white/4 text-white/40')
          }
          style={feedback === 'up' ? {
            borderColor: 'rgb(var(--primary-500) / 0.5)',
            background: 'rgb(var(--primary-500) / 0.25)',
            color: 'rgb(var(--primary-200))',
          } : undefined}
        >
          <ThumbsUp className="h-3 w-3" />
        </button>
        <button
          type="button"
          title="Nu"
          onClick={() => submitFeedback('down')}
          disabled={feedbackStatus === 'saving'}
          aria-pressed={feedback === 'down'}
          className={
            'grid h-6 w-6 place-items-center rounded-md border transition-all disabled:opacity-50 ' +
            (feedback === 'down'
              ? 'sf-recap-fb-active-down'
              : 'sf-recap-icon-btn border-white/6 bg-white/4 text-white/40')
          }
          style={feedback === 'down' ? {
            borderColor: 'rgba(220, 60, 60, 0.5)',
            background: 'rgba(220, 60, 60, 0.2)',
            color: 'rgb(254, 202, 202)',
          } : undefined}
        >
          <ThumbsDown className="h-3 w-3" />
        </button>
      </div>

      <style jsx>{`
        @keyframes shine {
          from { background-position: 100% 0; }
          to { background-position: -100% 0; }
        }
        .sf-recap-icon-btn:hover {
          border-color: rgb(var(--primary-500) / 0.4) !important;
          background-color: rgb(var(--primary-500) / 0.15) !important;
          color: rgb(var(--primary-200)) !important;
        }
        .sf-recap-primary-btn:hover {
          box-shadow: 0 6px 16px rgb(var(--primary-500) / 0.35);
        }
      `}</style>
    </article>

    {/* Drawer preview factură — deschis PESTE dashboard la click pe o factură din
        briefing; fără navigare la pagina-listă. Închiderea revine pe dashboard.
        Randat prin PORTAL la <body> ca overlay-ul (fixed inset-0) să acopere TOT
        viewport-ul, inclusiv bara de sus — altfel un container părinte cu
        transform/filter „prinde" elementul fixed și îl confinează sub header. */}
    {typeof document !== 'undefined' && createPortal(
      <DocumentPreviewModal
        type="invoice"
        documentId={drawerInvoiceId}
        onClose={() => setDrawerInvoiceId(null)}
        onSwitchDocument={(id) => setDrawerInvoiceId(id)}
      />,
      document.body,
    )}
    </>
  );
}
